
import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Settings, ZoomIn, ZoomOut, SkipForward, SkipBack, PlaySquare, Eye, EyeOff, Maximize, X, LogOut } from 'lucide-react';
import { PromoCard } from '../../promotions/components/PromoCard';
import { layoutStyleFromTelaConfiguracoes, extrairDescricaoDoJson, TIPO_TO_LAYOUT, extrairPosicaoDoJson, extrairPaginacaoDoJson, extrairZoomDoJson, extrairNumeroColunasDoJson, extrairLarguraColunaDoJson } from '../../banners/services/bannerSyncService';
import { mergePanfletoContentPreserving } from '../../banners/utils/panfletoContentUtils';
import { bannerService, TelaBanner, ProdutoBanner, resolveProdutoBannerCategoriaNome } from '../../banners/services/bannerService';
import { clearPanfletoFotoBlobCache } from '../../banners/utils/panfletoProdutoFotoBlobCache';
import { useStore } from '../../../store/useStore';
import { Screen, Promotion, LayoutType } from '../../../shared/types';
import { totemModeService } from '../../../shared/services/totemModeService';
import styles from './DisplayPage.module.css';
import { televisaoService } from '../../televisao/services/televisaoService';
import { useTelevisaoWebSocket } from '../../televisao/hooks/useTelevisaoWebSocket';
import { useDeviceDetection } from '../../../shared/hooks/useDeviceDetection';
import { usePriceUpdate } from '../hooks/usePriceUpdate';
import { authService } from '../../auth/services/authService';
import { useTotemSessionExpiryRecovery } from '../../../shared/hooks/useTotemSessionExpiryRecovery';
import { useInchSimulation } from '../hooks/useInchSimulation';
import { DisplayChromePortal, InchSimulationBadge, InchSimulationControl, InchSimulationStage } from '../components/InchSimulationControl';

// Memo para reduzir re-renderizações durante transições e paginações
const MemoPromoCard = React.memo(PromoCard);

/** Chave YYYY-MM-DD no fuso local — troca na meia-noite para religar o filtro de telas por dia da semana. */
function getLocalCalendarDayKey(date: Date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// Lista de tipos de unidade (mesma lista dos filtros de promoção)
const TIPOS_UNIDADE_SIGLAS = ['UN', 'KG', 'MT', 'M2', 'M3', 'LT', 'ML', 'SC', 'FD'];

// Função para remover siglas de tipo de unidade do final do nome do produto
// Apenas para visualização - não altera os dados originais
const removeUnitTypeFromName = (name: string): string => {
  if (!name || typeof name !== 'string') return name;
  
  const trimmedName = name.trim();
  if (!trimmedName) return name;
  
  // Verificar se o nome termina com qualquer sigla (case-insensitive)
  for (const sigla of TIPOS_UNIDADE_SIGLAS) {
    // Verificar se termina com espaço + sigla (case-insensitive)
    const regex = new RegExp(`\\s+${sigla.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`, 'i');
    if (regex.test(trimmedName)) {
      // Remover a sigla do final
      return trimmedName.replace(regex, '').trim();
    }
  }
  
  return name;
};

// Componente para carregar vídeo com autenticação (mesmo da SequenceDisplayPage)
const VideoPlayerWithAuth: React.FC<{
  screenId: number;
  videoBlobUrl?: string | null;
  videoLoop: boolean;
  onPlay: () => void;
  onPause: () => void;
  onEnded: () => void;
  externalVideoRef?: React.MutableRefObject<HTMLVideoElement | null>;
  isPaused?: boolean;
}> = ({ screenId, videoBlobUrl: preloadedVideoUrl, videoLoop, onPlay, onPause, onEnded, externalVideoRef, isPaused = false }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoBlobUrl, setVideoBlobUrl] = useState<string | null>(preloadedVideoUrl || null);
  const [loading, setLoading] = useState(!preloadedVideoUrl);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (preloadedVideoUrl && preloadedVideoUrl.trim() !== '') {
      setVideoBlobUrl(preloadedVideoUrl);
      setLoading(false);
      setError(null);
      return;
    }

    let blobUrl: string | null = null;

    const loadVideo = async () => {
      try {
        setLoading(true);
        setError(null);

        const videoBlob = await bannerService.downloadVideo(screenId);
        if (videoBlob && videoBlob.size > 0) {
          blobUrl = URL.createObjectURL(videoBlob);
          setVideoBlobUrl(blobUrl);
        } else {
          setError('Vídeo não encontrado ou arquivo vazio');
        }
      } catch (err) {
        console.error('Erro ao carregar vídeo:', err);
        setError(err instanceof Error ? err.message : 'Erro ao carregar vídeo');
      } finally {
        setLoading(false);
      }
    };

    if (!preloadedVideoUrl) {
      loadVideo();
    }

    return () => {
      if (blobUrl && blobUrl !== preloadedVideoUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [screenId, preloadedVideoUrl]);

  useEffect(() => {
    if (videoRef.current) {
      const video = videoRef.current;
      video.playbackRate = 1.0;
      if (video.readyState >= 2) {
        video.currentTime = 0;
      }
    }
  }, [screenId, videoBlobUrl]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (videoRef.current) {
        const video = videoRef.current;
        video.playbackRate = 1.0;
        if (!video.paused) {
          video.playbackRate = 1.0;
        }
      }
    }, 0);

    return () => clearTimeout(timer);
  }, [videoBlobUrl, screenId]);

  useEffect(() => {
    if (!videoRef.current || !videoBlobUrl || loading || error) return;

    if (isPaused) {
      if (videoRef.current && !videoRef.current.paused) {
        videoRef.current.pause();
      }
      return;
    }

    const video = videoRef.current;
    video.playbackRate = 1.0;

    const startPlayback = async () => {
      if (isPaused) {
        return;
      }

      try {
        video.playbackRate = 1.0;
        video.muted = true;
        await video.play();
        video.playbackRate = 1.0;

        try {
          video.muted = false;
        } catch (unmuteCheck) {
          console.warn('Autoplay unmuting failed - waiting for user interaction');
        }

        onPlay();
      } catch (playError) {
        console.error('Autoplay muted also failed:', playError);
      }
    };

    if (video.readyState >= 3) {
      startPlayback();
    } else {
      video.addEventListener('canplay', startPlayback, { once: true });

      return () => {
        video.removeEventListener('canplay', startPlayback);
      };
    }
  }, [videoBlobUrl, loading, error, onPlay, isPaused]);

  useEffect(() => {
    if (!videoRef.current || !videoBlobUrl) return;

    const video = videoRef.current;

    if (isPaused) {
      if (!video.paused) {
        video.pause();
      }

      const handlePlayAttempt = (e: Event) => {
        if (isPaused) {
          e.preventDefault();
          video.pause();
        }
      };

      video.addEventListener('play', handlePlayAttempt);

      return () => {
        video.removeEventListener('play', handlePlayAttempt);
      };
    }
  }, [isPaused, videoBlobUrl]);

  useEffect(() => {
    if (videoRef.current) {
      const video = videoRef.current;
      video.playbackRate = 1.0;
    }
  }, [videoBlobUrl]);

  useEffect(() => {
    if (!videoRef.current || !videoBlobUrl) return;

    const video = videoRef.current;
    video.playbackRate = 1.0;

    const handleRateChange = () => {
      if (video.playbackRate !== 1.0) {
        console.warn('PlaybackRate alterado! Corrigindo para 1.0');
        video.playbackRate = 1.0;
      }
    };

    const handleTimeUpdate = () => {
      if (video.playbackRate !== 1.0) {
        video.playbackRate = 1.0;
      }
    };

    const handleLoadedMetadata = () => {
      video.playbackRate = 1.0;
    };

    const handleCanPlay = () => {
      video.playbackRate = 1.0;
    };

    video.addEventListener('ratechange', handleRateChange);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('canplay', handleCanPlay);

    return () => {
      video.removeEventListener('ratechange', handleRateChange);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('canplay', handleCanPlay);
    };
  }, [videoBlobUrl, screenId]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <p className="text-xl text-gray-500">Carregando vídeo...</p>
      </div>
    );
  }

  if (error || !videoBlobUrl) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <p className="text-xl text-gray-500">{error || 'Nenhum vídeo encontrado para esta tela'}</p>
      </div>
    );
  }

  return (
    <div className={styles.videoContainer}>
      <video
        ref={(el) => {
          if (el) {
            (videoRef as React.MutableRefObject<HTMLVideoElement | null>).current = el;
            if (externalVideoRef) {
              externalVideoRef.current = el;
            }
            el.playbackRate = 1.0;
            el.defaultPlaybackRate = 1.0;
          } else {
            (videoRef as React.MutableRefObject<HTMLVideoElement | null>).current = null;
            if (externalVideoRef) {
              externalVideoRef.current = null;
            }
          }
        }}
        src={videoBlobUrl}
        className="w-full h-full object-contain"
        loop={videoLoop}
        autoPlay
        muted
        playsInline
        controls={false}
        onLoadStart={(e) => {
          e.currentTarget.playbackRate = 1.0;
        }}
        onLoadedMetadata={(e) => {
          e.currentTarget.playbackRate = 1.0;
        }}
        onCanPlay={(e) => {
          e.currentTarget.playbackRate = 1.0;
        }}
        onPlay={(e) => {
          const video = e.currentTarget;
          video.playbackRate = 1.0;
          onPlay();
        }}
        onPause={onPause}
        onEnded={onEnded}
      />
    </div>
  );
};

const TelevisaoDisplayPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { promotions, showHeader, setShowHeader, displayConfig, updateDisplayConfig } = useStore();

  const isTotemMode = totemModeService.isTotemMode();

  // Só totem: sessão de TV expirada → QR de reconexão
  useTotemSessionExpiryRecovery(isTotemMode);

  // Detecção de dispositivo e ajustes de zoom/resolução
  const { deviceInfo } = useDeviceDetection();
  const inchSimulation = useInchSimulation();

  // Tipo para armazenar tela carregada com suas promoções e vídeo pré-carregado
  type LoadedScreen = {
    screen: Screen;
    promotions: Promotion[];
    videoBlobUrl?: string | null;
    position?: { posicaoX?: number; posicaoY?: number };
    productsPerPage?: number;
    tempoTelevisao?: number; // Tempo configurado na televisão para esta tela
    tempoFinal?: number; // Tempo final a ser usado (apenas tempoTelevisao, undefined = roda infinitamente)
    zoomLevel?: number;
    numeroColunas?: number;
    larguraColuna?: number;
    /** JSON de configurações (mesmo do salvamento do panfleto) — exibição do cartaz. */
    configuracoes?: string;
  };

  const [televisaoId, setTelevisaoId] = useState<number | null>(null);
  const [televisaoNome, setTelevisaoNome] = useState<string>('');
  const [currentScreenIndex, setCurrentScreenIndex] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [tvPanfletoProductIndex, setTvPanfletoProductIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false); // Estado para controlar transição de produtos
  const [loadedScreens, setLoadedScreens] = useState<LoadedScreen[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState({ loaded: 0, total: 0 });
  const [progress, setProgress] = useState(0);
  const [isPlayingVideo, setIsPlayingVideo] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isTelevisaoPaused, setIsTelevisaoPaused] = useState(false);
  const [showPagination, setShowPagination] = useState(false);
  const [nomeDiaAtual, setNomeDiaAtual] = useState<string>('');
  /** Incrementado na meia-noite, ao detectar “dia agora ≠ último carregamento OK”, etc. — força novo GET da grade do dia. */
  const [scheduleResyncNonce, setScheduleResyncNonce] = useState(0);
  /** Após merge SignalR das telas — refetch da foto de fundo do panfleto (PromoCard). */
  const [panfletoTelaFotoReloadToken, setPanfletoTelaFotoReloadToken] = useState(0);

  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const paginationTimerRef = useRef<NodeJS.Timeout | null>(null);
  const productPaginationTimerRef = useRef<NodeJS.Timeout | null>(null);
  const panfletoTvProductTimerRef = useRef<NodeJS.Timeout | null>(null);
  /** Índice atual do produto no panfleto (para o timer não depender de closure obsoleta ao decidir VT). */
  const tvPanfletoProductIndexRef = useRef(0);
  const videoRefForPause = useRef<HTMLVideoElement | null>(null);
  const progressBarRef = useRef<HTMLDivElement | null>(null);

  // Proteção para View Transitions API (evitar reentrância/estado inválido)
  const viewTransitionInProgressRef = useRef<boolean>(false);
  const lastVTTimeRef = useRef<number>(0);
  const runSafeViewTransition = useCallback((updateFn: () => void) => {
    const hasVT = typeof document !== 'undefined' && 'startViewTransition' in document;
    if (!hasVT) {
      updateFn();
      return;
    }
    if (document.visibilityState !== 'visible') {
      updateFn();
      return;
    }
    try {
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        updateFn();
        return;
      }
    } catch {}
    const now = performance.now?.() || Date.now();
    if (now - (lastVTTimeRef.current || 0) < 80) {
      updateFn();
      return;
    }
    if (viewTransitionInProgressRef.current) {
      updateFn();
      return;
    }
    requestAnimationFrame(() => {
      try {
        viewTransitionInProgressRef.current = true;
        const vt = (document as any).startViewTransition(() => {
          updateFn();
        });
        Promise.resolve(vt?.finished)
          .catch(() => {})
          .finally(() => {
            viewTransitionInProgressRef.current = false;
            lastVTTimeRef.current = performance.now?.() || Date.now();
          });
      } catch {
        viewTransitionInProgressRef.current = false;
        updateFn();
      }
    });
  }, []);

  const telasCacheRef = useRef<Map<number, any>>(new Map());
  const videoBlobCacheRef = useRef<Map<number, string>>(new Map());
  const loadingAbortControllerRef = useRef<AbortController | null>(null);
  // Ref para armazenar dados da API de cada tela
  const telasAPIDataRef = useRef<Map<number, TelaBanner>>(new Map());
  /** Última data+dia da semana para as quais `loadTelevisaoScreens` terminou com sucesso (evita ficar preso se timers falharem). */
  const appliedScheduleFingerprintRef = useRef<string | null>(null);

  useEffect(() => {
    return () => {
      clearPanfletoFotoBlobCache();
    };
  }, []);

  // Safe screen index
  const safeScreenIndex = useMemo(() => {
    if (loadedScreens.length === 0) return 0;
    if (currentScreenIndex >= loadedScreens.length) return 0;
    if (currentScreenIndex < 0) return 0;
    return currentScreenIndex;
  }, [currentScreenIndex, loadedScreens.length]);

  useEffect(() => {
    if (loadedScreens.length > 0) {
      const maxIndex = loadedScreens.length - 1;
      if (currentScreenIndex > maxIndex) {
        setCurrentScreenIndex(0);
      } else if (currentScreenIndex < 0) {
        setCurrentScreenIndex(0);
      }
    } else if (currentScreenIndex !== 0) {
      setCurrentScreenIndex(0);
    }

    setCurrentPage(0);

    if (loadedScreens.length > 0 && loadedScreens[safeScreenIndex]) {
      const entry = loadedScreens[safeScreenIndex];
      const screenZoom =
        entry.screen.layoutId === 'panfleto' ? 1 : (entry.zoomLevel ?? 1);
      if (displayConfig.zoomLevel !== screenZoom) {
        updateDisplayConfig({ zoomLevel: screenZoom });
      }
    }
  }, [loadedScreens.length, currentScreenIndex, safeScreenIndex, loadedScreens, displayConfig.zoomLevel, updateDisplayConfig]);

  const currentScreen = useMemo(() => {
    if (loadedScreens.length === 0) return null;
    return loadedScreens[safeScreenIndex]?.screen || null;
  }, [loadedScreens, safeScreenIndex]);

  const activePromotions = useMemo(() => {
    if (loadedScreens.length === 0) return [];
    return loadedScreens[safeScreenIndex]?.promotions || [];
  }, [loadedScreens, safeScreenIndex]);

  useEffect(() => {
    setTvPanfletoProductIndex(0);
  }, [currentScreenIndex, currentScreen?.id]);

  useEffect(() => {
    setTvPanfletoProductIndex(i => {
      if (activePromotions.length <= 0) return 0;
      return Math.min(i, activePromotions.length - 1);
    });
  }, [activePromotions.length]);

  useEffect(() => {
    tvPanfletoProductIndexRef.current = tvPanfletoProductIndex;
  }, [tvPanfletoProductIndex]);

  // Calcular paginação
  const totalPages = useMemo(() => {
    if (loadedScreens.length === 0) return 1;

    const promotions = loadedScreens[safeScreenIndex]?.promotions || [];
    const screen = loadedScreens[safeScreenIndex]?.screen;
    const itemsPerPage = loadedScreens[safeScreenIndex]?.productsPerPage || 20;

    if (!screen || screen.layoutId !== 'simple2col') {
      return 1;
    }

    const safeItemsPerPage = itemsPerPage > 0 ? itemsPerPage : 20;
    return Math.ceil(promotions.length / safeItemsPerPage);
  }, [loadedScreens, safeScreenIndex]);

  // Calcular produtos da página atual
  const paginatedPromotions = useMemo(() => {
    if (loadedScreens.length === 0) return [];

    const promotions = loadedScreens[safeScreenIndex]?.promotions || [];
    const screen = loadedScreens[safeScreenIndex]?.screen;
    const itemsPerPage = loadedScreens[safeScreenIndex]?.productsPerPage || 20;

    if (!screen || screen.layoutId !== 'simple2col') {
      return promotions;
    }

    const safeItemsPerPage = itemsPerPage > 0 ? itemsPerPage : 20;
    const calculatedTotalPages = Math.max(1, Math.ceil(promotions.length / safeItemsPerPage));

    if (calculatedTotalPages <= 1) {
      return promotions;
    }

    const safeCurrentPage = Math.min(currentPage, calculatedTotalPages - 1);
    const startIndex = safeCurrentPage * safeItemsPerPage;
    return promotions.slice(startIndex, startIndex + safeItemsPerPage);
  }, [loadedScreens, safeScreenIndex, currentPage]);

  // Meia-noite local: recarrega a grade do dia (não depende só de estado “calendar key”).
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const scheduleNextMidnightReload = () => {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
      const now = new Date();
      const nextMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0, 0);
      const ms = Math.max(1000, nextMidnight.getTime() - now.getTime());
      timeoutId = setTimeout(() => {
        setScheduleResyncNonce((n) => n + 1);
        scheduleNextMidnightReload();
      }, ms);
    };

    scheduleNextMidnightReload();

    return () => {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  // Watchdog: se o relógio/data mudou e o último carregamento OK era de outro dia, força resync (TV standby, timers atrasados, etc.).
  useEffect(() => {
    const scheduleFingerprintNow = () => {
      const n = new Date();
      return `${getLocalCalendarDayKey(n)}-${n.getDay()}`;
    };

    const checkScheduleDrift = () => {
      const applied = appliedScheduleFingerprintRef.current;
      if (applied === null) return;
      const cur = scheduleFingerprintNow();
      if (cur !== applied) {
        setScheduleResyncNonce((n) => n + 1);
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        checkScheduleDrift();
      }
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pageshow', checkScheduleDrift);
    window.addEventListener('focus', checkScheduleDrift);

    const FIVE_MIN_MS = 5 * 60 * 1000;
    const intervalId = window.setInterval(checkScheduleDrift, FIVE_MIN_MS);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pageshow', checkScheduleDrift);
      window.removeEventListener('focus', checkScheduleDrift);
      window.clearInterval(intervalId);
    };
  }, []);

  // Carregar televisão e suas telas vinculadas
  useEffect(() => {
    if (loadingAbortControllerRef.current) {
      loadingAbortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    loadingAbortControllerRef.current = abortController;

    setInitialLoading(true);
    setLoadingProgress({ loaded: 0, total: 0 });
    setLoadedScreens([]);
    setCurrentScreenIndex(0);
    setCurrentPage(0);

    const commitAppliedScheduleFingerprint = () => {
      const n = new Date();
      appliedScheduleFingerprintRef.current = `${getLocalCalendarDayKey(n)}-${n.getDay()}`;
    };

    const loadTelevisaoScreens = async () => {
      if (!id) {
        setInitialLoading(false);
        return;
      }

      try {
        const televisaoIdNum = parseInt(id, 10);
        if (isNaN(televisaoIdNum)) {
          setInitialLoading(false);
          return;
        }

        setTelevisaoId(televisaoIdNum);

        // Buscar televisão
        const televisao = await televisaoService.buscarTelevisao(televisaoIdNum);
        setTelevisaoNome(televisao.nmTelevisao);

        // Extrair configurações (tempos e ordens) das telas da televisão
        const configsTelevisao = (televisaoService as any).extrairTodasConfigs?.(televisao.dsConfiguracoes) || new Map();
        const temposTelevisao = televisaoService.extrairTodosTempos(televisao.dsConfiguracoes);

        // Extrair IDs das telas do ds_telas_cadastradas
        let telaIds = televisaoService.stringToTelaIds(televisao.dsTelasCadastradas);
        
        // Obter dia da semana atual (0=Domingo, 1=Segunda, ..., 6=Sábado)
        const hoje = new Date();
        const diaSemanaAtual = hoje.getDay().toString(); // Converter para string para comparar com os dias configurados
        
        // Nomes dos dias da semana para exibição
        const nomesDiasSemana = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];
        const nomeDia = nomesDiasSemana[hoje.getDay()];
        setNomeDiaAtual(nomeDia);
        
        // IMPORTANTE: Filtrar telas pelo dia da semana atual
        // Se uma tela não tiver dias configurados ou tiver array vazio, ela será ignorada (não aparece)
        const telaIdsFiltradas = telaIds.filter(telaId => {
            const config = configsTelevisao.get(telaId);
            const diasTela = config?.dias;
            
            // Se não tiver dias configurados ou array vazio, ignorar a tela (não aparecer)
            if (!diasTela || diasTela.length === 0) {
                return false;
            }
            
            // Verificar se o dia atual está nos dias configurados
            return diasTela.includes(diaSemanaAtual);
        });
        
        // IMPORTANTE: Ordenar telaIds pela ordem configurada na televisão
        const telaIdsComOrdem = telaIdsFiltradas.map(telaId => {
            const config = configsTelevisao.get(telaId);
            return {
                id: telaId,
                ordem: config?.ordem || 0
            };
        });
        
        // Ordenar por ordem (do menor para o maior, mesmo com gaps)
        telaIdsComOrdem.sort((a, b) => {
            if (a.ordem > 0 && b.ordem > 0) {
                return a.ordem - b.ordem;
            }
            if (a.ordem > 0) return -1;
            if (b.ordem > 0) return 1;
            return 0; // Manter ordem original se não tiver ordem definida
        });
        
        telaIds = telaIdsComOrdem.map(item => item.id);

        if (telaIds.length === 0) {
          // IMPORTANTE: Garantir que loadedScreens está vazio, initialLoading é false e loadingProgress está zerado
          setLoadedScreens([]);
          setLoadingProgress({ loaded: 0, total: 0 });
          setInitialLoading(false);
          commitAppliedScheduleFingerprint();
          return;
        }

        setLoadingProgress({ loaded: 0, total: telaIds.length });

        // Carregar cada tela individualmente na ordem do ds_telas_cadastradas
        const loadedScreensData: LoadedScreen[] = [];

        for (let i = 0; i < telaIds.length; i++) {
          if (abortController.signal.aborted) {
            return;
          }

          const telaId = telaIds[i];

          try {
            let telaAPI: any = null;

            if (telasCacheRef.current.has(telaId)) {
              telaAPI = telasCacheRef.current.get(telaId);
            } else {
              const telasAPI = await bannerService.listarTelas(telaId);
              telaAPI = telasAPI.find(t => t.id === telaId);

              if (telaAPI && telaAPI.id !== undefined) {
                telasCacheRef.current.set(telaAPI.id, telaAPI);
                // Armazenar também no ref de dados da API
                telasAPIDataRef.current.set(telaAPI.id, telaAPI);
              }
            }

            if (!telaAPI || !telaAPI.id) {
              setLoadingProgress(prev => ({ loaded: prev.loaded + 1, total: prev.total }));
              continue;
            }

            // Converter para Screen
            const layoutId = telaAPI.tipo !== undefined
              ? (TIPO_TO_LAYOUT[telaAPI.tipo] || 'simple2col') as LayoutType
              : 'simple2col' as LayoutType;

            let description = telaAPI.descricao || '';
            if (!description && telaAPI.configuracoes) {
              const descricaoDoJson = extrairDescricaoDoJson(telaAPI.configuracoes);
              if (descricaoDoJson) {
                description = descricaoDoJson;
              }
            }

            const layoutStyle = layoutStyleFromTelaConfiguracoes(telaAPI.configuracoes);
            const position = extrairPosicaoDoJson(telaAPI.configuracoes);
            const paginacao = extrairPaginacaoDoJson(telaAPI.configuracoes);
            const productsPerPage = paginacao && paginacao > 0 ? paginacao : 20;

            // IMPORTANTE: Extrair apenas o tempo configurado na televisão para esta tela
            // Não usar mais o tempo de paginação da tela como fallback
            // Se não tiver tempo configurado na televisão, será undefined (tela roda infinitamente)
            const tempoTelevisao = temposTelevisao.get(telaId);
            // Garantir que é um número válido
            const tempoFinal = tempoTelevisao !== undefined && tempoTelevisao !== null && !isNaN(tempoTelevisao) && tempoTelevisao > 0
                ? Number(tempoTelevisao)
                : undefined;
            

            const zoomSalvo = extrairZoomDoJson(telaAPI.configuracoes);
            const zoomLevel = zoomSalvo !== undefined ? zoomSalvo : 1;

            const numeroColunasSalvo = extrairNumeroColunasDoJson(telaAPI.configuracoes);
            const numeroColunas = (layoutId === 'simple2col' && numeroColunasSalvo !== undefined && numeroColunasSalvo >= 1 && numeroColunasSalvo <= 3) 
              ? numeroColunasSalvo 
              : (layoutId === 'simple2col' ? 2 : undefined);
            
            const larguraColunaSalva = extrairLarguraColunaDoJson(telaAPI.configuracoes);
            const larguraColuna = (numeroColunas === 1 && larguraColunaSalva !== undefined && larguraColunaSalva >= 20 && larguraColunaSalva <= 100)
              ? larguraColunaSalva
              : (numeroColunas === 1 ? 100 : undefined);

            const isVideo = layoutId === 'video';

            const fullScreen: Screen = {
              id: telaAPI.id,
              name: telaAPI.nome,
              description: description,
              layoutId: layoutId,
              promotions: telaAPI.produtoIds ? telaAPI.produtoIds.map((p: any) => p.produtoId || p) : [],
              isActive: telaAPI.ativo ?? false,
              backgroundColor: '#ffffff',
              backgroundImage: undefined,
              showHeader: true,
              duration: 30, // Não usado para televisão, cada tela tem seu próprio tempo
              layoutStyle: layoutStyle,
              createdAt: new Date().toISOString()
            };

            // Buscar promoções da API
            let screenPromotions: Promotion[] = [];
            if (telaAPI.produtoIds && Array.isArray(telaAPI.produtoIds) && telaAPI.produtoIds.length > 0) {
              screenPromotions = telaAPI.produtoIds.map((produto: any) => {
                const produtoId = produto.produtoId || produto;
                const nomeOriginal = produto.nome || 'Produto sem nome';
                // IMPORTANTE: Remover sigla de tipo de unidade do final do nome apenas para visualização
                const nomeLimpo = removeUnitTypeFromName(nomeOriginal);
                const categoriaDaApi =
                  typeof produto === 'object' && produto !== null
                    ? resolveProdutoBannerCategoriaNome(produto as ProdutoBanner & Record<string, unknown>)
                    : '';

                const promotion: Promotion = {
                  id: typeof produtoId === 'number' ? produtoId : (produto.produtoId || 0),
                  name: nomeLimpo,
                  price: produto.vlPreco || 0,
                  originalPrice: produto.vlPrecoNormal || undefined,
                  unit: produto.tipoUnidade || 'UN',
                  image: undefined,
                  video: undefined,
                  description: undefined,
                  category: categoriaDaApi || 'Geral',
                  validUntil: produto.dtFinalCampanha
                    ? new Date(produto.dtFinalCampanha).toISOString()
                    : '',
                  validFrom:
                    produto.dtInicialCampanha || produto.dtInicioCampanha
                      ? new Date(produto.dtInicialCampanha || produto.dtInicioCampanha).toISOString()
                      : undefined,
                  isVideoPromotion: false,
                  hasAudio: false,
                  autoplay: false,
                  loop: false,
                  createdAt: new Date().toISOString()
                };

                const localPromo = useStore.getState().promotions.find(p => p.id === promotion.id);
                if (localPromo) {
                  // IMPORTANTE: Remover sigla de tipo de unidade do nome do store local também
                  const nomeLocalLimpo = removeUnitTypeFromName(localPromo.name || nomeOriginal);
                  const storeCat = (localPromo.category || '').trim();
                  const categoryFromStore =
                    storeCat && storeCat !== 'Sem categoria' ? storeCat : '';

                  return {
                    ...promotion,
                    name: nomeLocalLimpo,
                    image: localPromo.image,
                    video: localPromo.video,
                    description: localPromo.description,
                    category: categoriaDaApi || categoryFromStore || 'Geral',
                    isVideoPromotion: localPromo.isVideoPromotion,
                    hasAudio: localPromo.hasAudio,
                    autoplay: localPromo.autoplay,
                    loop: localPromo.loop
                  };
                }

                return promotion;
              });
            }

            // Pré-carregar vídeo se for tela de vídeo
            let videoBlobUrl: string | null = null;
            if (isVideo && telaAPI.id) {
              try {
                if (videoBlobCacheRef.current.has(telaAPI.id)) {
                  videoBlobUrl = videoBlobCacheRef.current.get(telaAPI.id) || null;
                } else {
                  const videoBlob = await bannerService.downloadVideo(telaAPI.id);
                  if (videoBlob && videoBlob.size > 0) {
                    videoBlobUrl = URL.createObjectURL(videoBlob);
                    videoBlobCacheRef.current.set(telaAPI.id, videoBlobUrl);
                  }
                }
              } catch (videoError) {
                console.error(`Erro ao pré-carregar vídeo da tela ${telaAPI.id}:`, videoError);
              }
            }

            setLoadingProgress(prev => ({ loaded: prev.loaded + 1, total: prev.total }));

            const loadedScreen = {
              screen: fullScreen,
              promotions: screenPromotions,
              videoBlobUrl: videoBlobUrl,
              position: position,
              productsPerPage: productsPerPage,
              tempoTelevisao: tempoTelevisao, // Tempo configurado na televisão
              tempoFinal: tempoFinal, // Tempo final a ser usado (undefined = roda infinitamente)
              zoomLevel: zoomLevel,
              numeroColunas: numeroColunas,
              larguraColuna: larguraColuna,
              configuracoes: telaAPI.configuracoes,
            } as LoadedScreen;

            loadedScreensData.push(loadedScreen);
          } catch (error) {
            console.error(`Erro ao processar tela ${telaId}:`, error);
            setLoadingProgress(prev => ({ loaded: prev.loaded + 1, total: prev.total }));
          }
        }

        setLoadedScreens(loadedScreensData);
        commitAppliedScheduleFingerprint();
      } catch (error) {
        console.error('Erro ao carregar televisão:', error);
      } finally {
        setInitialLoading(false);
      }
    };

    loadTelevisaoScreens();

    return () => {
      if (loadingAbortControllerRef.current) {
        loadingAbortControllerRef.current.abort();
        loadingAbortControllerRef.current = null;
      }
    };
  }, [id, scheduleResyncNonce]);

  // Callback para quando o SignalR detectar mudanças nas telas da televisão
  const handleScreensUpdated = useCallback((updatedScreens: typeof loadedScreens) => {
    // Função utilitária para deep-merge do priceBox (preserva campos antigos quando absent/undefined)
    const mergePriceBox = (prevPB: any | undefined, incomingPB: any | undefined) => {
      if (!prevPB && !incomingPB) return undefined;
      const base = {
        enabled: false,
        backgroundColor: '#ffffff',
        borderColor: '#000000',
        borderWidth: 0,
        borderRadius: 8,
        paddingTop: 4,
        paddingRight: 8,
        paddingBottom: 4,
        paddingLeft: 8,
        marginTop: 0,
        marginRight: 0,
        marginBottom: 0,
        marginLeft: 0,
      };
      return {
        ...base,
        ...(prevPB || {}),
        ...(incomingPB || {}),
      };
    };

    const mergedScreens = updatedScreens.map(us => {
      const prev = loadedScreens.find(ls => ls.screen.id === us.screen.id);
      const incomingLayoutStyle = (us.screen.layoutStyle || {}) as any;
      const previousLayoutStyle = (prev?.screen?.layoutStyle || {}) as any;

      const mergedLayoutStyle = {
        ...previousLayoutStyle,
        ...incomingLayoutStyle,
        priceBox: mergePriceBox(previousLayoutStyle?.priceBox, incomingLayoutStyle?.priceBox),
        panfletoContent: mergePanfletoContentPreserving(
          previousLayoutStyle?.panfletoContent,
          incomingLayoutStyle?.panfletoContent
        ),
      } as any;

      return {
        ...us,
        screen: {
          ...us.screen,
          layoutStyle: mergedLayoutStyle as any
        }
      };
    });

    if (mergedScreens.some((m) => m.screen.layoutId === 'panfleto')) {
      setPanfletoTelaFotoReloadToken((t) => t + 1);
    }

    setLoadedScreens(mergedScreens as any);
    
    // CRÍTICO: Se a lista estiver vazia (0 telas), ajustar estados para exibir a mensagem bonita
    // A mensagem aparece quando: !initialLoading && loadedScreens.length === 0 && loadingProgress.total === 0
    if (updatedScreens.length === 0) {
      setLoadingProgress({ loaded: 0, total: 0 });
      setInitialLoading(false);
    }
  }, []);

  // Callback para quando a configuração da televisão mudar (ordem, telas cadastradas, dias)
  // As alterações são aplicadas diretamente pelo hook, sem reiniciar a tela
  const handleTelevisaoConfigUpdated = useCallback((dsTelasCadastradas: string | null, dsConfiguracoes: string | null) => {
    // As alterações são aplicadas diretamente pelo hook useTelevisaoWebSocket
  }, []);

  // Configurar SignalR para receber atualizações em tempo real da televisão
  // IMPORTANTE: Ativar SEMPRE que tiver televisaoId (mesmo com 0 telas, para detectar quando adicionar telas)
  // O hook precisa estar ativo mesmo sem telas para receber atualizações do SignalR quando telas forem adicionadas
  useTelevisaoWebSocket({
    televisaoId: televisaoId,
    loadedScreens: loadedScreens,
    telasAPIDataRef: telasAPIDataRef,
    onScreensUpdated: handleScreensUpdated,
    onTelevisaoConfigUpdated: handleTelevisaoConfigUpdated,
    promotions: promotions, // Passar promoções do store para preencher dados das telas
    enabled: !!televisaoId && !initialLoading // Ativar quando tiver televisaoId e não estiver carregando inicialmente (mesmo com 0 telas)
  });

  // Callback para atualizar preços quando receber evento ProdutoAtualizado
  // Atualiza preços em todas as telas carregadas
  const handlePricesUpdated = useCallback((precosAtualizados: any[]) => {
    if (!loadedScreens || loadedScreens.length === 0 || !precosAtualizados || precosAtualizados.length === 0) {
      return;
    }

    // Criar um mapa de preços atualizados por produtoId
    const precosMap = new Map<number, any>();
    precosAtualizados.forEach(produto => {
      const produtoId = produto.produtoId || produto.id;
      if (produtoId) {
        precosMap.set(produtoId, produto);
      }
    });

    // Atualizar preços em todas as telas carregadas
    const updatedScreens = loadedScreens.map(loadedScreen => {
      const updatedPromotions = loadedScreen.promotions.map(promo => {
        const precoAtualizado = precosMap.get(promo.id);
        
        if (precoAtualizado) {
          // Atualizar preços mantendo outros dados da promoção
          return {
            ...promo,
            price: precoAtualizado.vlPreco || promo.price,
            originalPrice: precoAtualizado.vlPrecoNormal || promo.originalPrice
          };
        }
        
        return promo;
      });

      return {
        ...loadedScreen,
        promotions: updatedPromotions
      };
    });

    // CRÍTICO: Atualizar loadedScreens com preços atualizados
    setLoadedScreens(updatedScreens);

    // Também atualizar cache de telas da API se necessário
    updatedScreens.forEach(loadedScreen => {
      const telaAPI = telasAPIDataRef.current.get(loadedScreen.screen.id);
      if (telaAPI && telaAPI.produtoIds) {
        const produtoIdsAtualizados = telaAPI.produtoIds.map((produto: any) => {
          const produtoId = produto.produtoId || produto.id;
          const precoAtualizado = precosMap.get(produtoId);
          
          if (precoAtualizado) {
            return {
              ...produto,
              vlPreco: precoAtualizado.vlPreco,
              vlPrecoNormal: precoAtualizado.vlPrecoNormal,
              dtPrecoAlterado: precoAtualizado.dtPrecoAlterado
            };
          }
          
          return produto;
        });

        telasAPIDataRef.current.set(loadedScreen.screen.id, {
          ...telaAPI,
          produtoIds: produtoIdsAtualizados
        });
      }
    });
  }, [loadedScreens]);

  // Obter IDs de todos os produtos de todas as telas carregadas
  const allProdutoIds = useMemo(() => {
    const ids = new Set<number>();
    loadedScreens.forEach(loadedScreen => {
      loadedScreen.promotions.forEach(promo => {
        if (promo.id) {
          ids.add(promo.id);
        }
      });
    });
    return Array.from(ids);
  }, [loadedScreens]);

  // Obter ID da primeira tela carregada (para registrar callback de atualização de preços)
  // O evento ProdutoAtualizado é global, então um único callback é suficiente
  const firstScreenId = useMemo(() => {
    return loadedScreens.length > 0 && loadedScreens[0]?.screen?.id ? loadedScreens[0].screen.id : null;
  }, [loadedScreens]);

  // Configurar SignalR para receber atualizações de preços em tempo real
  // Usar a primeira tela como referência (o evento é global e atualiza todas as telas)
  usePriceUpdate({
    screenId: firstScreenId,
    produtoIds: allProdutoIds, // Todos os produtos de todas as telas
    onPricesUpdated: handlePricesUpdated,
    enabled: !!firstScreenId && loadedScreens.length > 0 && allProdutoIds.length > 0 && !initialLoading
  });

  const advanceToNextScreenInner = useCallback(() => {
    if (loadedScreens.length === 0) return;

    setCurrentScreenIndex(prev => {
      const safePrev = prev >= loadedScreens.length ? 0 : (prev < 0 ? 0 : prev);

      const currentLoadedScreen = loadedScreens[safePrev];
      const promotions = currentLoadedScreen?.promotions || [];
      const itemsPerPage = currentLoadedScreen?.productsPerPage || 20;
      const safeItemsPerPage = itemsPerPage > 0 ? itemsPerPage : 20;
      const actualTotalPages = Math.max(1, Math.ceil(promotions.length / safeItemsPerPage));

      const nextPage = currentPage + 1;
      const nextPageHasProducts = nextPage < actualTotalPages;

      if (actualTotalPages > 1 && nextPageHasProducts) {
        setCurrentPage(nextPage);
        return safePrev;
      }

      setCurrentPage(0);
      const nextIndex = (safePrev + 1) % loadedScreens.length;
      return nextIndex;
    });
  }, [loadedScreens, currentPage]);

  const advanceToNextScreen = useCallback(() => {
    runSafeViewTransition(() => {
      advanceToNextScreenInner();
    });
  }, [runSafeViewTransition, advanceToNextScreenInner]);

  const handleVideoEnded = useCallback(() => {
    setIsPlayingVideo(false);
    if (!isTelevisaoPaused) {
      advanceToNextScreen();
    }
  }, [advanceToNextScreen, isTelevisaoPaused]);

  // Gerenciar paginação automática de produtos (usando tempo de paginação da tela)
  useEffect(() => {
    if (paginationTimerRef.current) {
      clearInterval(paginationTimerRef.current);
      paginationTimerRef.current = null;
    }
    if (productPaginationTimerRef.current) {
      clearTimeout(productPaginationTimerRef.current);
      productPaginationTimerRef.current = null;
    }

    setProgress(0);

    if (initialLoading || !currentScreen || loadedScreens.length === 0) {
      return;
    }

    if (isTelevisaoPaused) {
      return;
    }

    // Se é vídeo, não criar timer de paginação
    if (currentScreen.layoutId === 'video') {
      return;
    }

    // Apenas para telas de produtos (simple2col)
    if (currentScreen.layoutId !== 'simple2col') {
      return;
    }

    // IMPORTANTE: Verificar o tempo ANTES de verificar páginas
    // O tempo configurado na televisão é OBRIGATÓRIO para criar timer
    const currentLoadedScreen = loadedScreens[safeScreenIndex];
    const tempoFinal = currentLoadedScreen?.tempoFinal;
    
    // Se não tiver tempo configurado (tempoFinal é undefined), não criar timer
    // Isso significa que a tela vai rodar infinitamente (não avança automaticamente)
    if (tempoFinal === undefined || tempoFinal === null || isNaN(tempoFinal) || tempoFinal <= 0) {
      return;
    }
    
    // IMPORTANTE: O tempo configurado é para CADA PÁGINA da tela (ou para a tela inteira se tiver só 1 página)
    // Garantir que duration é um número válido
    const duration = Number(tempoFinal);
    if (isNaN(duration) || duration <= 0) {
      console.error(`📺 ERRO: Duration inválido: ${duration} (de ${tempoFinal})`);
      return;
    }
    
    const intervalTime = 100;

    // Configurar intervalo para barra de progresso
    // Atualizar barra de progresso sem re-render do React (imperativo)
    let percent = 0;
    paginationTimerRef.current = setInterval(() => {
      percent = Math.min(percent + (intervalTime / (duration * 1000)) * 100, 100);
      if (progressBarRef.current) {
        progressBarRef.current.style.width = `${percent}%`;
      }
    }, intervalTime);

    // Configurar timeout para avançar página ou tela
    // IMPORTANTE: O tempo configurado é aplicado para CADA PÁGINA (ou tela inteira se tiver só 1 página)
    const timeoutMs = duration * 1000;
    
    productPaginationTimerRef.current = setTimeout(() => {
      // Transição leve baseada apenas em opacidade/transform (sem View Transitions snapshots)
      const currentLoadedScreen = loadedScreens[safeScreenIndex];
      const promotions = currentLoadedScreen?.promotions || [];
      const itemsPerPage = currentLoadedScreen?.productsPerPage || 20;
      const safeItemsPerPage = itemsPerPage > 0 ? itemsPerPage : 20;
      const actualTotalPages = Math.max(1, Math.ceil(promotions.length / safeItemsPerPage));

      if (currentPage + 1 < actualTotalPages) {
        setCurrentPage(curr => curr + 1);
      } else {
        setCurrentPage(0);
        advanceToNextScreen();
      }
    }, timeoutMs);

      return () => {
        if (paginationTimerRef.current) {
          clearInterval(paginationTimerRef.current);
        }
        if (productPaginationTimerRef.current) {
          clearTimeout(productPaginationTimerRef.current);
        }
      };
    }, [currentPage, totalPages, currentScreen, loadedScreens, safeScreenIndex, initialLoading, isTelevisaoPaused, advanceToNextScreen]);

  // Panfleto na TV:
  // - usa sempre o tempo configurado na televisão (tempoFinal)
  // - com produtos: o tempo é aplicado por produto; no último, avança a tela
  // - sem produtos: aplica o mesmo tempo para avançar a tela
  useEffect(() => {
    if (panfletoTvProductTimerRef.current) {
      clearTimeout(panfletoTvProductTimerRef.current);
      panfletoTvProductTimerRef.current = null;
    }
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    if (progressBarRef.current) {
      progressBarRef.current.style.width = '0%';
    }

    if (initialLoading || !currentScreen || loadedScreens.length === 0 || isTelevisaoPaused) {
      return;
    }
    if (currentScreen.layoutId !== 'panfleto') {
      return;
    }

    const ls = loadedScreens[safeScreenIndex];
    const n = ls?.promotions?.length ?? 0;
    const tempoFinal = ls?.tempoFinal;
    if (tempoFinal === undefined || tempoFinal === null || isNaN(tempoFinal) || tempoFinal <= 0) {
      return;
    }
    const duracao = Math.max(1, Number(tempoFinal));
    const intervalTime = 100;
    let percent = 0;
    progressIntervalRef.current = setInterval(() => {
      percent = Math.min(percent + (intervalTime / (duracao * 1000)) * 100, 100);
      if (progressBarRef.current) {
        progressBarRef.current.style.width = `${percent}%`;
      }
    }, intervalTime);

    panfletoTvProductTimerRef.current = setTimeout(() => {
      // Igual ao DisplayPage: troca de produto no mesmo panfleto sem View Transition
      // (evita flash branco / snapshot); VT só ao mudar de tela — animação padrão slide.
      if (n <= 0) {
        runSafeViewTransition(() => {
          setTvPanfletoProductIndex(0);
          advanceToNextScreenInner();
        });
        return;
      }
      const prev = tvPanfletoProductIndexRef.current;
      if (prev >= n - 1) {
        runSafeViewTransition(() => {
          setTvPanfletoProductIndex(0);
          advanceToNextScreenInner();
        });
      } else {
        setTvPanfletoProductIndex(prev + 1);
      }
    }, duracao * 1000);

    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      if (panfletoTvProductTimerRef.current) {
        clearTimeout(panfletoTvProductTimerRef.current);
        panfletoTvProductTimerRef.current = null;
      }
    };
  }, [
    currentScreen?.layoutId,
    currentScreen?.id,
    loadedScreens,
    safeScreenIndex,
    tvPanfletoProductIndex,
    isTelevisaoPaused,
    initialLoading,
    advanceToNextScreenInner,
    runSafeViewTransition,
  ]);

  // Controlar pausa de vídeos quando a televisão for pausada
  useEffect(() => {
    if (!videoRefForPause.current) return;

    const video = videoRefForPause.current;

    if (isTelevisaoPaused) {
      if (!video.paused) {
        video.pause();
      }

      const handlePlay = () => {
        if (isTelevisaoPaused && !video.paused) {
          video.pause();
        }
      };

      video.addEventListener('play', handlePlay);

      return () => {
        video.removeEventListener('play', handlePlay);
      };
    } else {
      if (video.paused && video.readyState >= 2 && video.currentTime < video.duration) {
        video.play().catch(err => {
          console.error('Erro ao dar play no vídeo após despausar:', err);
        });
      }
    }
  }, [isTelevisaoPaused]);

  // Funções de zoom
  const handleZoom = useCallback((increase: boolean) => {
    const currentZoom = displayConfig.zoomLevel || 1;
    const step = 0.1;
    const minZoom = 0.5;
    const maxZoom = 2.0;

    const newZoom = increase
      ? Math.min(currentZoom + step, maxZoom)
      : Math.max(currentZoom - step, minZoom);

    updateDisplayConfig({ zoomLevel: newZoom });
  }, [displayConfig.zoomLevel, updateDisplayConfig]);

  const handleTotemLogout = useCallback(() => {
    authService.logout();
    totemModeService.disableTotemMode();
    navigate('/televisao', { replace: true });
  }, [navigate]);

  // Listener de teclado para atalhos
  useEffect(() => {
    const toggleFullscreen = () => {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen();
        setIsFullscreen(true);
      } else {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    };

    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'f' || e.key === 'F') {
        toggleFullscreen();
      } else if ((e.key === 'c' || e.key === 'C') && !isTotemMode) {
        setShowHeader(!showHeader);
      } else if ((e.key === '+' || e.key === '=') && currentScreen?.layoutId !== 'panfleto') {
        handleZoom(true);
      } else if ((e.key === '-' || e.key === '_') && currentScreen?.layoutId !== 'panfleto') {
        handleZoom(false);
      } else if (e.key === 'm' || e.key === 'M') {
        setShowSettings(!showSettings);
      } else if (e.key === 'p' || e.key === 'P') {
        setShowPagination(prev => !prev);
      } else if (e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        setIsTelevisaoPaused(prev => !prev);
      } else if (e.key === 'ArrowRight' && loadedScreens.length > 0) {
        advanceToNextScreen();
      } else if (e.key === 'ArrowLeft' && loadedScreens.length > 0) {
        setCurrentScreenIndex(prev => {
          const safePrev = prev >= loadedScreens.length ? 0 : (prev < 0 ? 0 : prev);
          const prevIndex = safePrev === 0 ? loadedScreens.length - 1 : safePrev - 1;
          return prevIndex;
        });
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [showHeader, setShowHeader, displayConfig.zoomLevel, showSettings, handleZoom, loadedScreens.length, advanceToNextScreen, isTelevisaoPaused, isTotemMode, currentScreen?.layoutId]);

  // Listener para mudanças de fullscreen
  useEffect(() => {
    setIsFullscreen(!!document.fullscreenElement);

    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const renderContent = () => {
    // IMPORTANTE: Verificar primeiro se não há telas (após o loading terminar)
    // Isso deve ser verificado ANTES do shouldShowLoading para evitar loading infinito
    if (!initialLoading && loadedScreens.length === 0 && loadingProgress.total === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-full px-4">
          <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-6 max-w-md text-center">
            <div className="text-4xl mb-4">📺</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Nenhuma tela disponível
            </h2>
            <p className="text-lg text-gray-600 mb-1">
              Não há telas configuradas para exibição
            </p>
            {nomeDiaAtual && (
              <p className="text-base text-gray-700 font-semibold mb-4">
                no dia de <span className="text-blue-600">{nomeDiaAtual}</span>
              </p>
            )}
            <p className="text-sm text-gray-500 mb-4">
              Configure os dias da semana para cada tela na televisão <strong>{televisaoNome}</strong> para que elas apareçam nos dias desejados.
            </p>
            {!isTotemMode && (
              <button
                onClick={() => navigate('/televisoes')}
                className="mt-4 bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition-colors font-medium"
              >
                Voltar para Gerenciar Televisões
              </button>
            )}
          </div>
        </div>
      );
    }

    const shouldShowLoading = initialLoading ||
      (loadedScreens.length === 0 && loadingProgress.total > 0) ||
      !currentScreen;

    const currentLoadedScreen = loadedScreens[safeScreenIndex];
    const currentPosition = currentLoadedScreen?.position;
    const topOffset = currentPosition?.posicaoY ?? 0;
    const leftOffset = currentPosition?.posicaoX ?? 0;

    if (shouldShowLoading) {
      const progressPercent = loadingProgress.total > 0
        ? Math.round((loadingProgress.loaded / loadingProgress.total) * 100)
        : 0;

      return (
        <div className="flex flex-col items-center justify-center w-full h-full min-h-screen bg-black">
          <div className="animate-spin rounded-full h-20 w-20 border-b-4 border-blue-500 mb-8"></div>
          <p className="text-3xl text-white font-bold mb-4">Carregando televisão</p>
          {loadingProgress.total > 0 ? (
            <>
              <p className="text-xl text-gray-300 mb-6">
                {loadingProgress.loaded} de {loadingProgress.total} telas carregadas
              </p>
              <div className="w-80 h-3 bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500 transition-all duration-300 ease-out"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <p className="text-lg text-gray-400 mt-4">{progressPercent}%</p>
            </>
          ) : (
            <p className="text-xl text-gray-300">Identificando telas da televisão...</p>
          )}
        </div>
      );
    }

    // Vídeo
    if (currentScreen.layoutId === 'video') {
      const loadedScreenData = loadedScreens.find(ls => ls.screen.id === currentScreen.id) || loadedScreens[safeScreenIndex];

      return (
        <VideoPlayerWithAuth
          screenId={currentScreen.id}
          videoBlobUrl={loadedScreenData?.videoBlobUrl || null}
          videoLoop={false}
          onPlay={() => setIsPlayingVideo(true)}
          onPause={() => setIsPlayingVideo(false)}
          onEnded={handleVideoEnded}
          externalVideoRef={videoRefForPause}
          isPaused={isTelevisaoPaused}
        />
      );
    }

    // Panfleto
    if (currentScreen.layoutId === 'panfleto') {
      const hasPanfletoProducts = activePromotions.length > 0;
      const safeIdx = hasPanfletoProducts
        ? Math.min(tvPanfletoProductIndex, activePromotions.length - 1)
        : 0;
      const currentPromo: Promotion = hasPanfletoProducts
        ? activePromotions[safeIdx]
        : {
            id: 0,
            name: '',
            price: 0,
            unit: '',
            validUntil: '',
            category: '',
            createdAt: new Date().toISOString(),
          };

      // Mesmo conjunto de classes que DisplayPage (`getPanfletoProductTransitionClasses`): animação do cartaz vem do PromoCard / panfleto.
      const panfletoProductTrans = `${styles.panfletoProductShell} ${styles.panfletoProductsVt} ${styles.panfletoProductOuter}`;

      const borderRadiusTv = currentScreen?.layoutStyle?.productContainer?.borderRadius;

      return (
        <div
          className={styles.centeredLayout}
          style={{
            top: `${topOffset}px`,
            left: `${leftOffset}px`
          }}
        >
          <div
            className={`${styles.productCard} ${panfletoProductTrans}`}
            style={{
              borderRadius: borderRadiusTv ? `${borderRadiusTv}px` : undefined,
              overflow: 'visible',
            }}
          >
            <PromoCard
              promo={currentPromo}
              layout="panfleto"
              layoutStyle={currentScreen.layoutStyle}
              panfletoProdutoFotoViaApi={hasPanfletoProducts}
              panfletoHasProductsOnScreen={hasPanfletoProducts}
              panfletoTelaId={currentScreen.id}
              panfletoTelaFotoReloadToken={panfletoTelaFotoReloadToken}
            />
          </div>
        </div>
      );
    }

    // Simple2col (padrão)
    const loadedScreenData = loadedScreens.find(ls => ls.screen.id === currentScreen.id) || loadedScreens[safeScreenIndex];
    const numeroColunasAtual = loadedScreenData?.numeroColunas ?? 2;
    const larguraColunaAtual = loadedScreenData?.larguraColuna ?? (numeroColunasAtual === 1 ? 100 : undefined);
    
    const getGridClasses = () => {
      if (numeroColunasAtual === 1) {
        return "grid grid-cols-1";
      } else if (numeroColunasAtual === 3) {
        // COMPORTAMENTO ESPECIAL: TV + 3 colunas configuradas
        // SEMPRE divide a largura disponível em 3 colunas iguais, independente do tamanho
        if (deviceInfo.isTV) {
          return "grid"; // Apenas grid, o estilo inline vai dividir em 3
        } else {
          // Para não-TV: usar breakpoints responsivos do Tailwind (comportamento padrão)
          return "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3";
        }
      } else {
        // Padrão: 2 colunas - também adapta automaticamente (comportamento padrão)
        return "grid grid-cols-1 md:grid-cols-2";
      }
    };

    const getColSpanClass = () => {
      if (numeroColunasAtual === 1) {
        return "col-span-1";
      } else if (numeroColunasAtual === 3) {
        // Para TV + 3 colunas: não precisa de col-span, cada item ocupa 1 coluna automaticamente
        if (deviceInfo.isTV) {
          return ""; // Vazio, cada item ocupa 1/3 automaticamente
        } else {
          // Para não-TV: usar col-span-3 (comportamento padrão)
          return "col-span-3";
        }
      } else {
        return "col-span-2";
      }
    };
    
    // COMPORTAMENTO ESPECIAL: Estilo inline para TV + 3 colunas
    // Divide automaticamente a largura disponível em 3 colunas iguais
    const getGridStyle = () => {
      if (deviceInfo.isTV && numeroColunasAtual === 3) {
        return {
          gridTemplateColumns: 'repeat(3, 1fr)' // Divide em 3 colunas iguais, independente do tamanho
        };
      }
      return {};
    };

    return (
      <div
        className={`${styles.listLayout} w-full p-6`}
        style={{
          position: 'relative',
          top: `${topOffset}px`,
          left: `${leftOffset}px`
        }}
      >
        <div 
          key={`tv-products-${currentScreen?.id ?? 'none'}-${currentPage}`}
          className={`${getGridClasses()} ${styles.productsSlideFade} ${styles.productsContainer}`}
          style={{
            overflow: 'hidden', // Garantir que o conteúdo não ultrapasse durante a animação
            // Forçar composição na GPU e isolar custos de layout/pintura
            willChange: 'opacity, transform',
            transform: 'translateZ(0)',
            backfaceVisibility: 'hidden',
            contain: 'layout paint style',
            ...getGridStyle(), // Aplica divisão automática para TV + 3 colunas
            gap: currentScreen?.layoutStyle?.productContainer?.verticalGap
              ? `${currentScreen.layoutStyle.productContainer.verticalGap}px ${currentScreen.layoutStyle.productContainer.horizontalGap || 8}px`
              : undefined,
            ...(numeroColunasAtual === 1 && larguraColunaAtual !== undefined ? { 
              maxWidth: larguraColunaAtual === 100 ? '100%' : `${larguraColunaAtual}%`, 
              margin: larguraColunaAtual === 100 ? '0' : '0 auto',
              width: '100%',
              paddingLeft: 0,
              paddingRight: 0,
              marginLeft: larguraColunaAtual === 100 ? 0 : 'auto',
              marginRight: larguraColunaAtual === 100 ? 0 : 'auto'
            } : {})
          }}
        >
          {paginatedPromotions.length === 0 ? (
            <div className={`${getColSpanClass()} flex items-center justify-center h-64`}>
              <p className="text-gray-500">Nenhuma promoção encontrada</p>
            </div>
          ) : (
            paginatedPromotions.map((promo, promoIndex) => (
              <div
                key={`${currentScreen?.id ?? 'tela'}-${currentPage}-${promo.id}-${promoIndex}`}
                className={`${styles.productCard} h-full w-full`}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  // Composição por GPU em cada card
                  willChange: 'opacity, transform',
                  transform: 'translateZ(0)',
                  backfaceVisibility: 'hidden',
                  contain: 'layout paint style',
                }}
              >
                <MemoPromoCard
                  promo={promo}
                  layout="simple2col"
                  layoutStyle={currentScreen.layoutStyle}
                />
              </div>
            ))
          )}
        </div>

        {/* Indicador de Paginação */}
        {!isTotemMode && currentScreen?.layoutId === 'simple2col' && showPagination && (
          <div
            className="fixed z-50 pointer-events-none"
            style={{
              top: '16px',
              left: '50%',
              transform: `translate(-50%, 0)`,
              transformOrigin: 'center top'
            }}
          >
            <div className="inline-flex items-center gap-4 px-6 py-2 rounded-lg bg-black/80 backdrop-blur-sm pointer-events-auto border border-white/20 text-white shadow-lg">
              <span className="font-medium text-lg">
                {totalPages === 1
                  ? `1 página (${activePromotions.length} produtos)`
                  : `Página ${Math.min(currentPage + 1, totalPages)} de ${totalPages}`
                }
              </span>
              <span className="text-xs opacity-60 ml-2 border-l border-white/30 pl-2 uppercase tracking-wide">
                (P) Ocultar
              </span>
            </div>
          </div>
        )}
      </div>
    );
  };

  const isCenteredLayout = currentScreen?.layoutId === 'video' || currentScreen?.layoutId === 'panfleto';
  const isLoading = initialLoading || (loadingProgress.total === 0 && loadedScreens.length === 0);

  useEffect(() => {
    if (isTotemMode && !isLoading) {
      setShowHeader(false);
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [isTotemMode, isLoading, setShowHeader]);

  return (
    <div 
      className={styles.displayContainer}
      style={{
        // Desabilitado para teste - não aplicar scale
        // ...(shouldApplyZoomCompensation ? {
        //   transform: `scale(${zoomCompensation})`,
        //   transformOrigin: 'top left',
        //   width: `${100 / zoomCompensation}%`,
        //   height: `${100 / zoomCompensation}%`
        // } : {}),
        // Permitir scroll no mobile se necessário
        ...(deviceInfo.isMobile ? {
          overflowX: 'auto',
          overflowY: 'auto'
        } : {
          overflow: 'hidden'
        })
      }}
    >
      <InchSimulationStage simulation={inchSimulation}>
      {/* Background Layer */}
      <div
        className={styles.backgroundLayer}
        style={{
          backgroundColor: currentScreen?.backgroundColor || '#000000',
          backgroundImage: (() => {
            if (currentScreen?.layoutId === 'panfleto') {
              return 'none';
            }

            const bgImage = currentScreen?.layoutStyle?.productContainer?.backgroundImage;
            const bgVisible = currentScreen?.layoutStyle?.productContainer?.backgroundImageVisible !== false;

            if (bgImage && typeof bgImage === 'string' && bgImage.trim().length > 0 && bgVisible && currentScreen?.layoutId !== 'video') {
              return `url(${bgImage})`;
            }

            return 'none';
          })(),
        }}
      />

      {/* Content Layer */}
      <div className={styles.contentLayer}>
        {/* Main Content */}
        <div className={styles.mainContent} style={{
          justifyContent: isLoading || isCenteredLayout ? 'center' : 'flex-start',
          alignItems: isLoading || isCenteredLayout ? 'center' : 'flex-start',
          display: 'flex',
          flexDirection: 'column',
          padding: isLoading
            ? '0'
            : !deviceInfo.isMobile &&
                currentScreen &&
                (currentScreen.layoutId === 'panfleto' || currentScreen.layoutId === 'video')
              ? '0'
              : '24px',
          // Desabilitado para teste - usar apenas zoomLevel
          transform: `scale(${currentScreen?.layoutId === 'panfleto' ? 1 : (displayConfig.zoomLevel || 1)})`,
          transformOrigin: 'center top',
          position: 'relative',
          // No mobile, sempre permitir scroll
          ...(deviceInfo.isMobile ? {
            overflowX: 'auto',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            minWidth: '100%',
            width: '100%'
          } : {
            // IMPORTANTE: Quando o cabeçalho estiver ativado (showHeader = true), ocultar scrolls para visualização limpa
            overflowY: (isLoading || isCenteredLayout || isTotemMode || showHeader) ? 'hidden' : 'auto',
            overflowX: showHeader ? 'hidden' : 'hidden' // Sempre ocultar scroll horizontal
          })
        }}>
          <div
            key={`tv-screen-${currentScreen?.id ?? 'loading'}-${currentScreen?.layoutId ?? ''}`}
            className={`${styles.screenSwapFade} ${styles.displayContentVt}${
              currentScreen?.layoutId === 'panfleto' ? ` ${styles.screenSwapFadePanfleto}` : ''
            }`}
          >
            {renderContent()}
          </div>
        </div>

        {/* Barra de Progresso - visível apenas quando o cabeçalho estiver visível */}
        {!isTotemMode && showHeader && currentScreen &&
          ((currentScreen.layoutId === 'simple2col' && totalPages > 1) ||
            (currentScreen.layoutId === 'panfleto' && activePromotions.length > 1)) && (
          <div className="fixed bottom-0 left-0 right-0 h-2 bg-gray-800 z-50">
            <div
              ref={el => (progressBarRef.current = el)}
              className="h-full bg-blue-500 transition-all duration-100 ease-linear"
              style={{ width: '0%' }}
            />
          </div>
        )}
      </div>
      </InchSimulationStage>
        <DisplayChromePortal>
        {/* Botões invisíveis de configuração/logout no modo totem */}
        {isTotemMode && (
          <>
            <button
              onClick={handleTotemLogout}
              className="absolute top-4 left-4 bg-transparent hover:bg-transparent focus:outline-none"
              style={{ zIndex: 1000, pointerEvents: 'auto' }}
              title="Sair do modo totem"
            >
              <LogOut size={24} className="text-white opacity-0 hover:opacity-100 transition-opacity duration-200" />
            </button>
            <div className={styles.settingsButton} style={{ zIndex: 1000 }}>
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="bg-transparent hover:bg-transparent focus:outline-none"
              title="Configurações de Exibição (M)"
            >
              <Settings size={24} className="text-white opacity-0 hover:opacity-100 transition-opacity duration-200" />
            </button>
            </div>
          </>
        )}

        {/* Settings Panel */}
        {showSettings && (
          <div className={styles.settingsPanel}>
            <div className={styles.settingsPanelHeader}>
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  Configurações de Exibição
                  <span className="text-sm text-gray-500 font-normal">(M)</span>
                </h3>
                <button
                  onClick={() => setShowSettings(false)}
                  className="text-gray-500 hover:text-gray-700 transition-colors p-1 rounded hover:bg-gray-100"
                  title="Fechar menu"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className={`${styles.settingsPanelContent} space-y-4`}>
              <InchSimulationControl simulation={inchSimulation} />

              {loadedScreens.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Televisão Ativa
                  </label>
                  <div className="flex justify-between items-center px-3 py-2 bg-blue-50 rounded-lg border border-blue-100">
                    <span className="font-medium text-blue-800">{televisaoNome}</span>
                    <div className="flex items-center gap-1 text-sm text-blue-600">
                      <span>{currentScreenIndex + 1} / {loadedScreens.length}</span>
                    </div>
                  </div>
                </div>
              )}

              <button
                onClick={() => setShowPagination(!showPagination)}
                className={`w-full bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center gap-2 ${!showPagination ? 'bg-gray-500 hover:bg-gray-600' : ''}`}
              >
                {showPagination ? <EyeOff size={18} /> : <Eye size={18} />}
                {showPagination ? 'Ocultar Paginação (P)' : 'Mostrar Paginação (P)'}
              </button>

              <button
                onClick={() => setIsTelevisaoPaused(!isTelevisaoPaused)}
                className="w-full bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center gap-2"
              >
                {isTelevisaoPaused ? 'Despausar' : 'Pausar'} televisão (Espaço)
              </button>

              {!isTotemMode && (
                <button
                  onClick={() => setShowHeader(!showHeader)}
                  className="w-full bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center gap-2"
                >
                  {showHeader ? 'Ocultar' : 'Mostrar'} Cabeçalho (C)
                </button>
              )}

              <button
                onClick={() => document.documentElement.requestFullscreen()}
                className="w-full bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
              >
                Modo Tela Cheia (F)
              </button>
            </div>
          </div>
        )}
        </DisplayChromePortal>

      <InchSimulationBadge simulation={inchSimulation} />

      {/* Fullscreen Overlay for Totem Mode */}
      {isTotemMode && !isFullscreen && !initialLoading && (
        <button
          type="button"
          className="fixed inset-0 z-[9999] w-full h-full bg-black bg-opacity-95 flex flex-col items-center justify-center cursor-pointer transition-opacity duration-500 focus:outline-none"
          onClick={() => {
            totemModeService.requestFullscreen().catch(err => console.error(err));

            if (videoRefForPause.current) {
              videoRefForPause.current.muted = false;
            }

            document.querySelectorAll('video').forEach(v => {
              try { v.muted = false; } catch (e) { }
            });
          }}
        >
          <div className="animate-bounce mb-8 p-6 bg-white bg-opacity-10 rounded-full">
            <Maximize size={64} className="text-white opacity-90" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 text-center px-4 drop-shadow-lg">
            Clique para ativar a Tela Cheia
          </h2>
        </button>
      )}
    </div>
  );
};

export default TelevisaoDisplayPage;
